import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const API_URL = 'accounts/admin/db-stats/';
const REFRESH_INTERVAL = 60000;

const C = {
  users:   { border: '#6366f1', text: '#a5b4fc' },
  blogs:   { border: '#e94560', text: '#f87171' },
  sessions:{ border: '#f77f00', text: '#fdba74' },
  gallery: { border: '#1b998b', text: '#34d399' },
};

function MetricCard({ label, value, color, sub }) {
  return (
    <div style={{
      background: '#1a1a2e',
      border: `2px solid ${color.border}`,
      borderRadius: 10,
      padding: '18px 22px',
      flex: '1 1 160px',
      minWidth: 140,
    }}>
      <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ color: color.text, fontSize: 32, fontWeight: 800, lineHeight: 1 }}>
        {value === null || value === undefined ? '—' : value.toLocaleString()}
      </div>
      {sub && (
        <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11, marginTop: 4 }}>{sub}</div>
      )}
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return '—';
  const diff = (Date.now() - new Date(iso)) / 1000;
  if (diff < 60) return Math.round(diff) + 's ago';
  if (diff < 3600) return Math.round(diff / 60) + 'm ago';
  if (diff < 86400) return Math.round(diff / 3600) + 'h ago';
  return Math.round(diff / 86400) + 'd ago';
}

export default function AdminDatabase() {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get(API_URL);
      setData(res.data);
      setLastFetched(new Date());
      setError(null);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        setError('Session expired. Please log in again.');
      } else {
        setError(e.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/signin?next=/admin/database'); return; }
    if (user && !user.is_staff && !user.is_superuser) { navigate('/'); return; }
    fetchData();
    const interval = setInterval(fetchData, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [isAuthenticated, user, fetchData, navigate]);

  if (user && !user.is_staff && !user.is_superuser) return <Navigate to="/" />;

  const metrics = data?.metrics || {};
  const tables = data?.tables || [];
  const totalRows = tables.reduce((sum, t) => sum + (t.row_count || 0), 0);

  const pageStyle = {
    minHeight: '100vh',
    background: '#0d0829',
    color: '#e2e8f0',
    fontFamily: "'Inter', -apple-system, sans-serif",
    padding: '32px 24px',
  };

  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
          <div>
            <h1 style={{ color: '#a855f7', margin: 0, fontSize: 26, fontWeight: 800, letterSpacing: -0.5 }}>
              Database Statistics
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.4)', margin: '4px 0 0', fontSize: 13 }}>
              Live row counts from pg_stat_user_tables
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <button
              onClick={fetchData}
              disabled={loading}
              style={{
                background: loading ? '#2d1f5e' : '#533483',
                color: '#e2e8f0',
                border: 'none',
                borderRadius: 6,
                padding: '7px 14px',
                cursor: loading ? 'default' : 'pointer',
                fontSize: 13,
              }}
            >
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
            {lastFetched && (
              <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11, marginTop: 4 }}>
                Updated {lastFetched.toLocaleTimeString()}
              </div>
            )}
          </div>
        </div>

        {error && (
          <div style={{
            background: '#2d1515',
            border: '1px solid #e94560',
            borderRadius: 8,
            padding: '12px 16px',
            color: '#f87171',
            marginBottom: 24,
            fontSize: 14,
          }}>
            Error: {error}
          </div>
        )}

        {/* Key Metric Cards */}
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 32 }}>
          <MetricCard label="Total Users"          value={metrics.total_users}         color={C.users}    sub="registered accounts" />
          <MetricCard label="Blog Posts"           value={metrics.total_blog_posts}    color={C.blogs}    sub="all statuses" />
          <MetricCard label="Chat Sessions"        value={metrics.total_sessions}      color={C.sessions} sub="Ubie conversations" />
          <MetricCard label="Gallery Images"       value={metrics.total_gallery_images} color={C.gallery}  sub="camels + wharf" />
          <MetricCard label="Total DB Rows"        value={loading ? null : totalRows}  color={{ border: '#b388ff', text: '#d8b4fe' }} sub="across all tables" />
        </div>

        {/* Table Stats */}
        <div style={{
          background: '#13102a',
          border: '1px solid rgba(168,85,247,0.25)',
          borderRadius: 10,
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '14px 20px',
            borderBottom: '1px solid rgba(168,85,247,0.15)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <span style={{ fontWeight: 700, color: '#c4b5fd', fontSize: 15 }}>
              All Tables ({tables.length})
            </span>
            <span style={{ color: 'rgba(255,255,255,0.35)', fontSize: 12 }}>
              Sorted by row count
            </span>
          </div>

          {loading && !data ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'rgba(255,255,255,0.35)' }}>
              Loading…
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'rgba(168,85,247,0.08)' }}>
                  <th style={{ textAlign: 'left', padding: '9px 20px', color: 'rgba(255,255,255,0.5)', fontWeight: 600, fontSize: 11, letterSpacing: 0.8 }}>
                    TABLE NAME
                  </th>
                  <th style={{ textAlign: 'right', padding: '9px 20px', color: 'rgba(255,255,255,0.5)', fontWeight: 600, fontSize: 11, letterSpacing: 0.8 }}>
                    ROW COUNT
                  </th>
                  <th style={{ textAlign: 'right', padding: '9px 20px', color: 'rgba(255,255,255,0.5)', fontWeight: 600, fontSize: 11, letterSpacing: 0.8 }}>
                    LAST VACUUMED
                  </th>
                </tr>
              </thead>
              <tbody>
                {tables.map((t, i) => (
                  <tr
                    key={t.table}
                    style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)' }}
                  >
                    <td style={{
                      padding: '8px 20px',
                      color: t.row_count > 0 ? '#e2e8f0' : 'rgba(255,255,255,0.35)',
                      fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                    }}>
                      {t.table}
                    </td>
                    <td style={{
                      padding: '8px 20px',
                      textAlign: 'right',
                      fontWeight: 600,
                      color: t.row_count > 100
                        ? '#a5b4fc'
                        : t.row_count > 0
                          ? '#e2e8f0'
                          : 'rgba(255,255,255,0.2)',
                    }}>
                      {(t.row_count || 0).toLocaleString()}
                    </td>
                    <td style={{
                      padding: '8px 20px',
                      textAlign: 'right',
                      color: 'rgba(255,255,255,0.35)',
                    }}>
                      {timeAgo(t.last_updated)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

      </div>
    </div>
  );
}
